import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import axios from 'axios';
import { viewerHeaders } from '../utils/viewerId';
import { optimizeCloudinaryUrl } from '../utils/cloudinaryOptimize';

const API = '/api';

// Per-placement max delivered width. These are the physical widest render
// widths across the site — Cloudinary will serve at this width and browsers
// downscale as needed. Doubles as a 2× DPR target for the smaller slots.
const PLACEMENT_WIDTH = {
  top_banner: 1200,
  footer_banner: 1200,
  in_feed: 1200,
  sidebar: 600,        // renders at max-w-[300px]
  blog_sidebar: 700,   // renders at max-w-[350px]
};

/**
 * AdBanner component for displaying advertisements
 * @param {string} placement - One of: 'top_banner' | 'sidebar' | 'in_feed' | 'footer_banner' | 'blog_sidebar'
 * @param {string} className - Additional CSS classes
 */
const AdBanner = ({ placement = 'top_banner', className = '' }) => {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adsDisabled, setAdsDisabled] = useState(false);
  const [impressionTracked, setImpressionTracked] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);
  const location = useLocation();

  // Trigger new fetch when location changes
  useEffect(() => {
    setFetchKey(prev => prev + 1);
  }, [location.key, location.pathname]);

  // Fetch ad whenever fetchKey changes
  useEffect(() => {
    const fetchAd = async () => {
      setLoading(true);
      setImpressionTracked(false);
      try {
        const response = await axios.get(`${API}/ads/${placement}?_t=${Date.now()}`);
        if (response.data?.disabled) {
          // Global ads kill-switch — render nothing, not even placeholder
          setAdsDisabled(true);
          setAd(null);
        } else {
          setAdsDisabled(false);
          setAd(response.data);
        }
      } catch (error) {
        setAdsDisabled(false);
        setAd(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [placement, fetchKey]);

  // Track impression when ad is displayed
  useEffect(() => {
    if (ad && !impressionTracked) {
      axios.post(`${API}/ads/${ad.id}/impression?placement=${placement}`, null, viewerHeaders()).catch(() => {});
      setImpressionTracked(true);
    }
  }, [ad, impressionTracked, placement]);

  // Build redirect URL for click tracking — the backend logs the click then 302s to the destination.
  // No vid parameter — viewer identity comes from the __emg_vid cookie (HttpOnly, set by platform).
  // This keeps the URL clean and avoids infinite crawlable URL variations in GSC.
  const clickTrackUrl = ad
    ? `${API}/ads/${ad.id}/redirect?placement=${placement}`
    : '#';

  // Placeholder content per placement type — using aspect ratios for correct proportions
  const placeholderContent = {
    top_banner: {
      dimension: 'w-full',
      imgClass: 'w-full h-auto',
      text: 'Advertise Your Business Here',
      subtext: 'Reach thousands of Essex residents daily'
    },
    sidebar: {
      dimension: 'w-full max-w-[300px] mx-auto',
      imgClass: 'w-full h-auto',
      text: 'Your Ad Here',
      subtext: 'Premium sidebar placement'
    },
    in_feed: {
      dimension: 'w-full',
      imgClass: 'w-full h-auto',
      text: 'Sponsored',
      subtext: 'Reach your local audience'
    },
    footer_banner: {
      dimension: 'w-full',
      imgClass: 'w-full h-auto',
      text: 'Advertise With Us',
      subtext: 'Footer banner — seen on every page'
    },
    blog_sidebar: {
      dimension: 'w-full max-w-[350px] mx-auto',
      imgClass: 'w-full h-auto',
      text: 'Your Ad Here',
      subtext: 'Blog sidebar placement'
    },
  };

  const content = placeholderContent[placement] || placeholderContent.top_banner;

  // Loading state
  if (loading) {
    return (
      <div data-testid={`ad-loading-${placement}`} className={`relative bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200 rounded-lg overflow-hidden ${content.dimension} ${className}`}>
        <div className="flex items-center justify-center py-6">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Global ads disabled — render nothing (no placeholder, no "Advertise Here" block)
  if (adsDisabled) return null;

  // Placeholder when no ads available
  if (!ad) {
    return (
      <div data-testid={`ad-placeholder-${placement}`} className={`relative bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200 rounded-lg overflow-hidden ${content.dimension} ${className}`}>
        <div className="absolute top-1 left-2 z-10">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium bg-white/80 px-1.5 py-0.5 rounded">
            Advertise Here
          </span>
        </div>
        <div className="flex items-center justify-center py-6">
          <div className="text-center px-4">
            <p className="text-slate-500 font-medium text-sm md:text-base">{content.text}</p>
            <p className="text-slate-400 text-xs mt-1">{content.subtext}</p>
            <a
              href="/advertise"
              className="inline-flex items-center gap-1 text-xs text-[#0F2C3E] hover:text-[#D4A017] mt-2 transition-colors"
              data-testid={`ad-advertise-here-${placement}`}
            >
              Advertise Here <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#0F2C3E]/5 rounded-full" />
        <div className="absolute -top-2 -left-2 w-16 h-16 bg-[#D4A017]/5 rounded-full" />
      </div>
    );
  }

  // Real ad — route the raw Cloudinary URL through the optimiser so we
  // get f_auto (WebP/AVIF where supported), q_auto (perceptual quality
  // compression) and a width bounded to the placement's max display.
  //
  // Previously we appended a `?_t=<fetchKey>` cache-buster on every route
  // change, which invalidated the browser cache and re-downloaded the full
  // untransformed asset on every page navigation. That single line was
  // responsible for ~44 GB of Cloudinary bandwidth on three animated-GIF
  // ad creatives. Removed. The parent fetch to /api/ads/:placement already
  // reloads on route change and returns a fresh ad.image_url, so genuine
  // rotation still works.
  const imgSrc = optimizeCloudinaryUrl(ad.image_url, {
    width: PLACEMENT_WIDTH[placement] || 1200,
    crop: 'limit',
    quality: 'auto',
  });

  return (
    <a
      href={clickTrackUrl}
      target="_blank"
      rel="nofollow sponsored noopener noreferrer"
      data-testid={`ad-display-${placement}`}
      className={`relative rounded-lg overflow-hidden ${content.dimension} ${className} cursor-pointer group block`}
    >
      <div className="absolute top-1 left-2 z-10">
        <span className="text-[10px] uppercase tracking-wider text-white/80 font-medium bg-black/40 px-1.5 py-0.5 rounded">
          Ad
        </span>
      </div>
      <img
        src={imgSrc}
        alt="Advertisement"
        loading="lazy"
        decoding="async"
        className={`${content.imgClass} object-contain transition-transform duration-300 group-hover:scale-[1.02]`}
        onError={(e) => {
          e.target.style.display = 'none';
          setAd(null);
        }}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 pointer-events-none" />
    </a>
  );
};

/**
 * InFeedAd - Shows between listings/posts on all devices.
 * Renders nothing (null) when ads are globally disabled or no ad is available,
 * so callers don't need to guard against empty grid cells.
 */
export const InFeedAd = ({ className = '' }) => (
  <AdBanner placement="in_feed" className={className} />
);

/**
 * SidebarAd - For sidebar placements (desktop only)
 */
export const SidebarAd = ({ className = '' }) => (
  <div className={`hidden md:block ${className}`} data-testid="sidebar-ad-wrapper">
    <AdBanner placement="sidebar" />
  </div>
);

/**
 * BlogSidebarAd - For blog page sidebar (desktop only)
 */
export const BlogSidebarAd = ({ className = '' }) => (
  <div className={`hidden md:block ${className}`} data-testid="blog-sidebar-ad-wrapper">
    <AdBanner placement="blog_sidebar" />
  </div>
);

/**
 * FooterAd - Site-wide footer banner
 */
export const FooterAd = ({ className = '' }) => (
  <div className={className} data-testid="footer-ad-wrapper">
    <AdBanner placement="footer_banner" />
  </div>
);

/**
 * TopBannerAd - Page-top banners
 */
export const TopBannerAd = ({ className = '' }) => (
  <div className={className} data-testid="top-banner-ad-wrapper">
    <AdBanner placement="top_banner" />
  </div>
);

export default AdBanner;
